import { CustomElement } from "../../core/CustomElement";
import "../date-display/index";
import Event, {
  EventFilter,
  EventRSVP,
  sortFilterUpcoming,
} from "../../core/interfaces/Event";
import "../link/index";
import "../forms/select-single/index";
import { CCCSelectSingle } from "../forms/select-single/index";
import SelectItem from "../../core/interfaces/SelectItem";
import {
  PageDirection,
  PaginationLink,
  SortOption,
} from "../../core/interfaces/Pagination";
import { enumToSelectItem } from "../../core/utilities";
import { CCCButton } from "../forms/button";
import EventModel from "../../models/EventModel";

export class CCCEventList extends CustomElement {
  $viewSelect: CCCSelectSingle = null;
  $sortBySelect: CCCSelectSingle = null;
  $rsvpSelect: CCCSelectSingle = null;
  rsvpCallback: (
    eventId: number,
    select: CCCSelectSingle,
    value: SelectItem
  ) => void;
  fetchEventsCallback: (
    startDate: string,
    endDate: string,
    myEvents: boolean,
    sort: SortOption,
    pageSize: number,
    page: number
  ) => void;
  viewOptions: Array<SelectItem> = enumToSelectItem(EventFilter);
  OGviewOptions: Array<SelectItem> = enumToSelectItem(EventFilter);
  viewOptionByDate: SelectItem = { id: 4, name: "" };
  rsvpOptions: Array<SelectItem> = enumToSelectItem(EventRSVP);
  sortByOptions: Array<SelectItem> = enumToSelectItem(sortFilterUpcoming);
  emptyOption: SelectItem = { id: -1, name: "" };
  firstLoad: boolean;
  ongoingEvents: boolean = false;
  urlParams = new URLSearchParams(window.location.search);
  isReplayTab = this.urlParams.get("tab") === "event-replays";

  static get observedAttributes(): Array<string> {
    return ["width"];
  }

  constructor() {
    super();
    this.templateConfig = {
      template: require("./template.html").default,
      style: require("./style.scss").default,
      viewModel: require("./viewModel"),
    };
    this.firstLoad = true;
    this.setupComponent();
    this.setLoading(true);
    this.initTabs();
    this.$viewSelect = this.shadowDom.querySelector("#viewSelect");
    this.$sortBySelect = this.shadowDom.querySelector("#sortSelect");
    this.$sortBySelect.setKeyState("options", this.sortByOptions);
    this.$sortBySelect.setValue(this.sortByOptions[0]); // Default to "All"

    this.$viewSelect.onItemSelect((value: SelectItem) => {
      this.viewModel.currentView$(value);
      if (value == this.viewOptions.at(-1)) {
        this.urlParams.set("tab", "event-replays");
      } else {
        this.urlParams.delete("tab");
      }
      const newUrl = `${window.location.pathname}?${this.urlParams.toString()}`;
      window.history.replaceState(null, "", newUrl);
      //Reset the second filter to all when switching tab
      this.$sortBySelect.setValue(this.sortByOptions[0]);
    });
    this.$sortBySelect.onItemSelect((value: SelectItem) => {
      this.viewModel.sortEventsByTime(value);
    });
    const pageSize = this.getAttribute("page-size");
    if (pageSize && Number(pageSize) > 0) {
      this.viewModel.isPaginationEnabled$(true);
      this.viewModel.pageSize = Number(pageSize);
      this.viewModel.currentPage = 1;
    }
  }

  attributeChangedCallback(
    attr: string,
    oldValue: string,
    newValue: string
  ): void {
    switch (attr) {
      case "width":
        this.setKeyState(attr, newValue);
        break;
      default:
        break;
    }
  }

  watchWidth(): void {
    const container = this.shadowDom.querySelector(
      "#eventListContainer"
    ) as HTMLElement;
    if (container) {
      container.style.width = this.state.width ? this.state.width : "";
    }
  }

  public setEvents(events: Array<Event>, myEvents = false): void {
    if (myEvents) {
      const filteredEvents = events.filter((event) => {
        return event.attending !== null;
      });
      this.viewModel.events$(filteredEvents);
    } else {
      this.viewModel.events$(events);
    }
    if (
      this.$viewSelect.getValue() === this.viewOptions[1] &&
      events.length === 0 &&
      this.firstLoad
    ) {
      //upcoming events empty, switch to all events
      this.$viewSelect.setValue(this.viewOptions[0]);
    }
    if (this.firstLoad) {
      this.firstLoad = false;
    }
  }

  public setLoading(loading: boolean): void {
    super.loading = loading;
    this.viewModel.loading$(loading);
  }

  public display(display: boolean): void {
    this.viewModel.display$(display);
  }

  public filterByDate(date: Date | string): void {
    if (typeof date === "string") {
      this.viewModel.dateFilter$(
        new Date(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10))
      );
    } else {
      this.viewModel.dateFilter$(date);
    }
    this.$viewSelect.setValue(this.emptyOption);
    this.viewModel.currentView$(this.viewOptionByDate);
  }

  setupRSVPSelects(rsvps: Array<CCCSelectSingle>): void {
    rsvps.map((rsvpSelect: CCCSelectSingle, index: number) => {
      const event: Event = this.viewModel.events$()[index];
      rsvpSelect.setKeyState("options", this.rsvpOptions);
      rsvpSelect.setKeyState("event", event);
      if (event.attending !== null) {
        switch (event.attending) {
          case "yes":
            rsvpSelect.setValue(this.rsvpOptions[0]);
            break;
          case "no":
            rsvpSelect.setValue(this.rsvpOptions[2]);
            break;
          case "maybe":
            rsvpSelect.setValue(this.rsvpOptions[1]);
            break;
        }
      }
      rsvpSelect.onItemSelect((value: SelectItem) => {
        if (this.rsvpCallback) {
          this.rsvpCallback(rsvpSelect.state.event.eventID, rsvpSelect, value);
        }
      });
    });
  }

  public onRSVP(
    callback: (
      eventId: number,
      select: CCCSelectSingle,
      value: SelectItem
    ) => void
  ): void {
    this.rsvpCallback = callback;
  }

  public onFetchEvents(
    callback: (
      startDate: string,
      endDate: string,
      myEvents: boolean,
      sort: SortOption,
      pageSize: number,
      page: number
    ) => void
  ): void {
    this.fetchEventsCallback = callback;
  }

  public init(): void {
    this.initTabs();
    if (this.fetchEventsCallback) {
      this.viewModel.currentView$(this.$viewSelect.getValue());
    }
  }

  private async initTabs() {
    const now = new Date();
    const startDate = "<" + now.toISOString().split(".")[0];
    const endDate = ">" + now.toISOString().split(".")[0];
    const pageSize = -1;
    const page = 1;

    try {
      const result = await EventModel.getEventsCached(
        startDate,
        endDate,
        startDate + "-" + endDate + "-initTabs",
        SortOption.asc,
        pageSize,
        page
      );

      const ongoingEvents = "events" in result ? result.events : result;

      if (ongoingEvents && ongoingEvents.length > 0) {
        this.ongoingEvents = true;
      } else {
        this.ongoingEvents = false;
      }
      this.setViewOptions(this.OGviewOptions);
    } catch (error) {
      console.error("Error fetching ongoing events in initTabs:", error);
      this.ongoingEvents = false;
    }
  }

  public setPagination(pagination: Map<string, PaginationLink>): void {
    this.viewModel.pagination$(pagination);
  }

  setupPreviousButton(button: CCCButton): void {
    button.addEventListener("ccc-button-click", () => {
      this.viewModel.gotoPage(PageDirection.previous);
    });
  }

  setupNextButton(button: CCCButton): void {
    button.addEventListener("ccc-button-click", () => {
      this.viewModel.gotoPage(PageDirection.next);
    });
  }

  public getCurrentPage(): number {
    return this.viewModel.currentPage;
  }

  public hasMore(eventList: Array<Event>): void {
    this.viewModel.isLastPage$(eventList.length === 0);
  }

  public async setViewOptions(viewOptions: Array<SelectItem>) {
    this.viewOptions = viewOptions;
    if (!this.ongoingEvents) {
      this.viewOptions = this.viewOptions.filter(
        (option) => option.name.toLowerCase() !== "live now"
      );
    }
    setTimeout(() => {
      this.$viewSelect.setKeyState("options", this.viewOptions);
      this.isReplayTab
        ? this.$viewSelect.setValue(this.viewOptions.at(-1))
        : this.$viewSelect.setValue(this.viewOptions[0]);
    });
  }

  public onViewChange(callback: (selectedView: SelectItem) => void): void {
    this.$viewSelect.onItemSelect((value: SelectItem) => {
      callback(value);
    });
  }
}

// define component
if (!customElements.get("ccc-event-list")) {
  customElements.define("ccc-event-list", CCCEventList);
}
